import React, { useContext as useContextCore, useMemo, useState } from 'react'
import { useBoolean } from 'react-use'
import type { ChannelMiddlewares, ChannelTypesComponent } from '../Channel'
import { RichTextChatInput } from '../Channel/RichTextChatInput'
import type { ChatComponents } from '../Chat/chatComponents'
import type { InsertMessageFunction } from '../Chat/Context'
import { NullComponent } from '@ztechapps/webui.util'
import { MessageContent } from './MessageContent'
import isEmpty from 'lodash/isEmpty'
import merge from 'lodash/merge'
import type {
  ChatDocumentKindEnum,
  Logistic,
  Order,
  PurchaseOrder,
  User,
  UserProfile,
} from '@ztechapps/common.contract'
import { ComposerOtherButtons } from '../Channel/ComposerOtherButtons'
import { composerToolbarInit } from '../Inbox/constants'
import { ComposerModeToggle } from './ComposerModeToggle'
import { ConversationComposerToolbarEnd } from './ConversationComposerToolbarEnd'
import { ConversationSendButton } from './ConversationSendButton'
import { LeftContent } from './LeftContent'
import { useComposerModeTint } from './useComposerModeTint'
import { useBuildDefaultTemplateVariables } from './useBuildDefaultTemplateVariables'
import type { SelectOption } from '@ztechapps/webui.input'
import type { ConfirmDialogOptions } from '@ztechapps/common.hook'
import type { UseSendChatMessageResult } from '@ztechapps/chat.common'
import type { MutationConfig } from '@ztechapps/common.utils'
import type { MenuPlacement } from 'react-select'

export enum ConversationForEnum {
  SalesOrder = 'SalesOrder',
  PurchaseOrder = 'PurchaseOrder',
  Logistic = 'Logistic',
}

// The composer's reply mode. Public reply is the default and the mode stays where the agent left it
// after a send.
export enum ComposerModeEnum {
  Public = 'Public reply',
  Note = 'Internal note',
}

export type TemplateVariables = {
  invoicePdfUrl?: () => Promise<string>
  invoiceUrl?: () => Promise<string>
  trackingNumbers?: string
  trackingNumbersWithUrl?: string
  resetPassword: (value?: any) => Promise<any>
  payWithCard: () => Promise<any>
  payWithBank: () => Promise<any>
  username: string
  profileName: string
  salesOrder: Order
  purchaseOrder: PurchaseOrder
  resetUsersPassword: SelectOption[]
  purchaseOrderPdfUrl?: () => Promise<string>
  purchaseOrderNumber?: string
  logistic?: Logistic
  logisticPdfUrl?: () => Promise<string>
  orderPackingSlipUrl?: () => Promise<string>
  logisticNumber?: string
}
export type ConversationProviderProps = {
  hasRole: boolean
  components?: ChannelTypesComponent
  children: React.ReactNode
  conversationId: string
  recipient: UserProfile
  subject: string
  members?: User[]
  updateTicketNoMutationConfig: MutationConfig
  documentId: string
  // The kind of the document-side host (sales order, purchase order, logistic): "Attach conversation" links
  // the picked chat to `documentId` of this kind. The inbox thread omits it — its record is the chat itself.
  documentKind?: ChatDocumentKindEnum
  templateCategory: string | string[]
  templateVariables: TemplateVariables
  templateVariableConfirmDialogConfigs?: Record<any, Partial<ConfirmDialogOptions>>
  sendChatMessageResult: UseSendChatMessageResult
  useFor: ConversationForEnum
  // Forwarded to `ChannelProviderProps.disabled`, ORed into ChannelProvider's `status.busy` beside
  // the provider's own internal-note save lock.
  disabled?: boolean
  // Called after the first Public reply from a record without a conversation succeeds, so the
  // host can re-read the record and pick up the conversation the reply created.
  onConversationCreated?: () => Promise<unknown>
  // Called after "Attach conversation" links a chat to the document, so the host can re-read the record
  // and pick up the document's new `activeConversationId`.
  onConversationLinked?: () => Promise<unknown>
  // The reply mode, held by a host whose provider remounts when the conversation changes (the
  // inbox) so the mode outlives the remount. Omitted, the provider keeps the mode itself.
  composerMode?: ComposerModeEnum
  setComposerMode?: (mode: ComposerModeEnum) => void
  /** Template picker rendered by the default composer action — provided by the host. */
  templateSelectComponent?: React.ComponentType<{
    insertMessage: InsertMessageFunction
    templateCategory: string | string[]
    templateVariables: TemplateVariables
    templateVariableConfirmDialogConfigs?: Record<any, Partial<ConfirmDialogOptions>>
    menuPlacement?: MenuPlacement
    disabled?: boolean
  }>
  /** Environment URLs, provided by the host (no app import in this package). */
  rootUrl?: string
  storeUrl?: string
}

export type ConversationContextType = {
  rootUrl?: string
  storeUrl?: string
  templateSelectComponent?: React.ComponentType<{
    insertMessage: InsertMessageFunction
    templateCategory: string | string[]
    templateVariables: TemplateVariables
    templateVariableConfirmDialogConfigs?: Record<any, Partial<ConfirmDialogOptions>>
    menuPlacement?: MenuPlacement
    disabled?: boolean
  }>
  middlewares: ChannelMiddlewares
  composerMode: ComposerModeEnum
  setComposerMode: (mode: ComposerModeEnum) => void
  // Reported by the note-save hook so `disabled` below locks the channel's editor for the save.
  setSavingNote: (saving: boolean) => void
} & Omit<
  ConversationProviderProps,
  | 'children'
  | 'rootUrl'
  | 'storeUrl'
  | 'templateSelectComponent'
  | 'onConversationCreated'
  | 'composerMode'
  | 'setComposerMode'
>

const context = React.createContext<ConversationContextType>({} as ConversationContextType)
const CoreProvider = context.Provider

// Module scope, never inline in the `useMemo` below: a host's inline `components` object re-runs
// that memo on every host render, and a fresh function per run is a new component type — React
// remounts the editor and the template select inside its header mid-action, so a pick's
// `generatedValue` is set on an unmounted instance and never reaches `insertMessage`.
// Precedent: libs/chat/webui/src/Chat/ChatButtons/AttachFileButton.tsx:19-21.
const ConversationComposerInput = () => {
  // The default composer serves the document-side mounts, so the toolbar jump gets this mount's
  // conversation id here; mounts with their own input (the inbox thread) omit it and the jump
  // stays unrendered.
  const { conversationId } = useConversationContext()
  const tintEditor = useComposerModeTint()
  return (
    <RichTextChatInput
      init={{
        menubar: false,
        ...composerToolbarInit,
        setup: (editor) => editor.on('init', () => tintEditor(editor)),
      }}
      toolbarStart={<ComposerModeToggle />}
      toolbarEnd={<ConversationComposerToolbarEnd conversationId={conversationId} canAttachConversation />}
    />
  )
}
const ConversationComposerOtherButtons = () => <ComposerOtherButtons variant="link" />

export const ConversationProvider = ({
  children,
  hasRole,
  components,
  documentId,
  templateVariables,
  templateSelectComponent,
  rootUrl,
  storeUrl,
  disabled,
  onConversationCreated,
  composerMode: hostComposerMode,
  setComposerMode: setHostComposerMode,
  ...props
}: ConversationProviderProps) => {
  //always merge the components, but make sure do it only when there are changes on the role
  //the default setting for input are triggered back
  const newComponents: ChannelTypesComponent = useMemo(() => {
    const defaultComponents: ChatComponents = {
      input: !hasRole ? NullComponent : ConversationComposerInput,
      // No row under the editor: the compact composer's controls live in the editor header
      // instead. The store's order Messages tab renders `Channel` directly (CommunicationTab.tsx)
      // and keeps `chatComponents`'s own `chatActionContainer`, so this override is scoped to
      // `ConversationProvider` mounts only.
      // COUPLING: COMPOSER_THREAD_CHROME_HEIGHT in libs/chat/webui/src/Inbox/constants.ts counts no height for
      // this container; one that renders a row adds its height there. The staged-attachments row
      // (`StagedAttachments`, `mt-2`) is not counted either — it shortens the message list only
      // while files are staged.
      chatActionContainer: NullComponent,
      sendMessageButton: ConversationSendButton,
      messageContent: MessageContent,
      chatActions: {
        leftContent: LeftContent,
        otherButtons: ConversationComposerOtherButtons,
      },
    }

    return merge({}, defaultComponents, components)
  }, [components, hasRole])

  const [ownComposerMode, setOwnComposerMode] = useState(ComposerModeEnum.Public)
  const storedComposerMode = hostComposerMode ?? ownComposerMode
  const setComposerMode = setHostComposerMode ?? setOwnComposerMode
  // Internal note is only in effect on a record that has a conversation. Every consumer reads this
  // effective mode; the stored one is never overwritten, so a note mode left over from another
  // record returns once a conversation exists.
  const composerMode = props.conversationId ? storedComposerMode : ComposerModeEnum.Public
  const [savingNote, setSavingNote] = useBoolean(false)

  const hooks = {
    // A send with no conversation id yet is the one that creates the conversation. The re-read is
    // started, never awaited: the channel clears the editor after this returns, and a slow or failed
    // re-read must neither delay nor skip that.
    afterSend: async () => {
      if (isEmpty(props.conversationId)) onConversationCreated?.().catch(() => undefined)
    },
  }

  const newTemplateVariables = useBuildDefaultTemplateVariables(templateVariables)

  return (
    <CoreProvider
      value={{
        components: newComponents,
        hasRole,
        rootUrl,
        storeUrl,
        templateSelectComponent,
        middlewares: hooks,
        composerMode,
        setComposerMode,
        setSavingNote,
        disabled: disabled || savingNote,
        documentId,
        templateVariables: newTemplateVariables,
        ...props,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useConversationContext = () => useContextCore(context)
